/**
 * Regions API Client
 * Strapi integration for region CRUD operations
 */

import { getHttpClient } from '@ufc/core'
import type {
  Region,
  RegionListItem,
  RegionStats,
  RegionsFilter,
  CreateRegionRequest,
  UpdateRegionRequest,
  PaginatedRegions,
  PaginationMeta,
  RegionSummary,
  CitySummary,
  FactorySummary,
  CountrySummary,
} from '../../domain/types'
import {
  REGION_ENDPOINTS,
  CITY_ENDPOINTS,
  REGION_POPULATE,
  type RegionListResponse,
  type RegionResponse,
  type FactoryDto,
} from '../../api/api-types'
import {
  transformDtoToDomain,
  transformDtoToListItem,
  transformListResponseToDomain,
  transformToCreateApiRequest,
  transformToUpdateApiRequest,
  transformFilterToQueryParams,
} from '../../api/transformers'

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_PAGE_SIZE = 20

// ============================================================================
// Helper Functions
// ============================================================================

function buildStrapiQueryString(params: Record<string, string | number | boolean>): string {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    searchParams.append(key, String(value))
  })
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

function calculateRegionStats(regions: RegionListItem[]): RegionStats {
  const byCountry: Record<string, number> = {}
  regions.forEach((r) => {
    if (r.countryName) {
      byCountry[r.countryName] = (byCountry[r.countryName] || 0) + 1
    }
  })

  return {
    total: regions.length,
    active: regions.filter((r) => r.isActive).length,
    inactive: regions.filter((r) => !r.isActive).length,
    byCountry,
  }
}

// ============================================================================
// Regions API
// ============================================================================

export const regionsApi = {
  /**
   * Get all regions with optional filtering
   */
  async getRegions(filter?: RegionsFilter): Promise<RegionListItem[]> {
    const http = getHttpClient()
    const filterParams = filter ? transformFilterToQueryParams(filter) : {}
    const queryParams = {
      ...filterParams,
      ...REGION_POPULATE.DEFAULT,
    }
    const queryString = buildStrapiQueryString(queryParams)
    const url = `${REGION_ENDPOINTS.LIST}${queryString}`

    const response = await http.get<RegionListResponse>(url)
    return response.data.data.map(transformDtoToListItem)
  },

  /**
   * Get paginated regions with filtering
   */
  async getRegionsPaginated(filter?: RegionsFilter): Promise<PaginatedRegions> {
    const http = getHttpClient()
    const page = filter?.page ?? 1
    const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE

    const filterParams = transformFilterToQueryParams({
      ...filter,
      page,
      pageSize,
    })
    const queryParams = {
      ...filterParams,
      ...REGION_POPULATE.DEFAULT,
    }
    const queryString = buildStrapiQueryString(queryParams)
    const url = `${REGION_ENDPOINTS.LIST}${queryString}`

    const response = await http.get<RegionListResponse>(url)
    return transformListResponseToDomain(response.data)
  },

  /**
   * Get single region by documentId with full relations
   */
  async getRegion(documentId: string): Promise<Region | null> {
    const http = getHttpClient()
    const queryString = buildStrapiQueryString(REGION_POPULATE.WITH_CITIES)
    const url = `${REGION_ENDPOINTS.DETAIL(documentId)}${queryString}`

    try {
      const response = await http.get<RegionResponse>(url)
      return transformDtoToDomain(response.data.data)
    } catch (error) {
      const axiosError = error as { response?: { status?: number } }
      if (axiosError.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  /**
   * Get region statistics
   */
  async getRegionStats(): Promise<RegionStats> {
    const regions = await regionsApi.getRegions()
    return calculateRegionStats(regions)
  },

  /**
   * Create a new region
   */
  async createRegion(data: CreateRegionRequest): Promise<Region> {
    const http = getHttpClient()
    const requestData = transformToCreateApiRequest(data)

    const response = await http.post<RegionResponse>(REGION_ENDPOINTS.CREATE, {
      data: requestData,
    })

    const documentId = response.data.data.documentId

    // Fetch the full region with populated relations
    const created = await regionsApi.getRegion(documentId)
    return created!
  },

  /**
   * Update an existing region
   */
  async updateRegion(documentId: string, data: UpdateRegionRequest): Promise<Region> {
    const http = getHttpClient()
    const updateData = transformToUpdateApiRequest(data)

    await http.put<RegionResponse>(REGION_ENDPOINTS.UPDATE(documentId), {
      data: updateData,
    })

    // Fetch the updated region with populated relations
    const updated = await regionsApi.getRegion(documentId)
    return updated!
  },

  /**
   * Delete a region (soft delete by setting isActive to false)
   */
  async deleteRegion(documentId: string): Promise<boolean> {
    const http = getHttpClient()

    try {
      await http.put<RegionResponse>(REGION_ENDPOINTS.UPDATE(documentId), {
        data: {
          isActive: false,
        },
      })
      return true
    } catch {
      return false
    }
  },

  /**
   * Hard delete a region (permanent)
   */
  async hardDeleteRegion(documentId: string): Promise<boolean> {
    const http = getHttpClient()

    try {
      await http.delete(REGION_ENDPOINTS.DELETE(documentId))
      return true
    } catch {
      return false
    }
  },

  /**
   * Search regions by query
   */
  async searchRegions(query: string): Promise<RegionListItem[]> {
    return regionsApi.getRegions({ search: query })
  },

  /**
   * Get regions by country
   */
  async getRegionsByCountry(countryId: string): Promise<RegionListItem[]> {
    return regionsApi.getRegions({ countryId })
  },

  /**
   * Get all regions as simple summaries (for dropdowns/multi-selects)
   */
  async getRegionSummaries(): Promise<RegionSummary[]> {
    const http = getHttpClient()
    const queryString = buildStrapiQueryString({
      'filters[isActive][$eq]': true,
      sort: 'name:asc',
    })
    const url = `${REGION_ENDPOINTS.LIST}${queryString}`

    const response = await http.get<RegionListResponse>(url)
    return response.data.data.map((dto) => ({
      id: String(dto.id),
      documentId: dto.documentId,
      code: dto.code,
      name: dto.name,
      isActive: dto.isActive ?? true,
    }))
  },
}

// ============================================================================
// Factories API (for region assignment)
// ============================================================================

export const factoriesForRegionsApi = {
  /**
   * Get all active factories for region assignment
   */
  async getActivefactories(): Promise<FactorySummary[]> {
    const http = getHttpClient()
    const queryString = buildStrapiQueryString({
      'filters[factoryStatus][$eq]': 'active',
      'filters[deletedAt][$null]': true,
      sort: 'name:asc',
    })
    const url = `/api/factories${queryString}`

    const response = await http.get<{ data: FactoryDto[] }>(url)
    return response.data.data.map((dto) => ({
      id: String(dto.id),
      documentId: dto.documentId,
      code: dto.code,
      name: dto.name, // Strapi returns name as string
      status: dto.factoryStatus ?? 'active',
    }))
  },
}

// ============================================================================
// Countries API (for region assignment)
// ============================================================================

interface CountryDto {
  id: number
  documentId: string
  code: string
  name: string
  isActive?: boolean
}

export const countriesForRegionsApi = {
  /**
   * Get all active countries for region selection
   */
  async getActiveCountries(): Promise<CountrySummary[]> {
    const http = getHttpClient()
    const queryString = buildStrapiQueryString({
      'filters[isActive][$eq]': true,
      sort: 'name:asc',
    })
    const url = `/api/countries${queryString}`

    const response = await http.get<{ data: CountryDto[] }>(url)
    return response.data.data.map((dto) => ({
      id: String(dto.id),
      documentId: dto.documentId,
      code: dto.code,
      name: dto.name,
      isActive: dto.isActive ?? true,
    }))
  },
}

// ============================================================================
// Cities API
// ============================================================================

interface CityListDto {
  id: number
  documentId: string
  code: string
  name: string
  isActive?: boolean
}

export const citiesApi = {
  /**
   * Get all active cities (sorted by name)
   */
  async getActiveCities(): Promise<CitySummary[]> {
    const http = getHttpClient()
    const queryString = buildStrapiQueryString({
      'filters[isActive][$eq]': true,
      sort: 'name:asc',
      'pagination[pageSize]': 200,
    })
    const url = `${CITY_ENDPOINTS.LIST}${queryString}`

    const response = await http.get<{ data: CityListDto[] }>(url)
    return response.data.data.map((dto) => ({
      id: String(dto.id),
      documentId: dto.documentId,
      code: dto.code,
      name: dto.name,
      isActive: dto.isActive ?? true,
    }))
  },
}
